import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import * as THREE from "three";

/* =====================================================================
   SNOW BACKGROUND — 3D watercolour snowfall for the login page
   ---------------------------------------------------------------------
   The canvas is TRANSPARENT: your existing night gradient + aurora
   (.login-page--aurora in App.css) stays exactly as it is and shows
   through. This layer only adds the 3D snow, a faint painted snowfield
   and soft mist, all tinted to match that palette.
   ===================================================================== */
const CONFIG = {
  // ---------- Snow (global multipliers, 1 = default) ----------
  SNOW_COUNT: 1.0,          // heavier snowfall: 1.5–2.5 · lighter: 0.4–0.7
  SNOW_SPEED: 1.0,          // fall speed: 0.5 = half speed
  WIND_STRENGTH: 1.0,       // sideways drift (0 = none)
  WIND_DIRECTION: 25,       // degrees: 0 = drifts to screen-right, 90 = toward screen-bottom
  SWAY_AMOUNT: 1.0,         // sine-wave floating / flutter amplitude
  FOREGROUND_SIZE: 1.0,     // size of the big soft flakes that pass near the camera
  SNOW_SOFTNESS: 1.0,       // depth-of-field blur on flakes (0.5 crisp … 2 very dreamy)
  SNOW_OPACITY: 0.85,       // overall flake opacity
  FLAKE_STYLE: "crystal",   // "crystal" = six-armed snowflakes · "mixed" = half crystals, half soft · "soft" = round watercolour drops
  TUMBLE_AMOUNT: 0.55,      // how much flakes tilt/flip as they fall (0 = flat, 1 = full flip)
  DISTANT_DOTS: true,       // far-away flakes become soft round specks (crystals only up close)

  // ---------- Aurora light & sparkle ----------
  AURORA_TINT: 0.3,         // how strongly flakes pick up the aurora colours (0 = pure white)
  TINT_TOP: "#93C5FD",      // blue near the top of the screen
  TINT_MID: "#C4B5FD",      // violet in the middle
  TINT_BOTTOM: "#F9A8D4",   // pink near the bottom
  SPARKLE_RATE: 0.04,       // share of flakes that glint each cycle (0 = no sparkle)
  SPARKLE_STRENGTH: 0.9,    // brightness of each glint
  SPARKLE_SPEED: 0.25,      // glint cycles per second per flake

  // ---------- Sign-in gust ----------
  GUST_STRENGTH: 14,        // sideways sweep when "Sign in with Google" is clicked (0 = off)

  // ---------- Watercolour ----------
  WATERCOLOR_STRENGTH: 1.0, // painted snowfield + flake rims (0 = no ground painting, 2 = strong)
  PAPER_GRAIN: 0.35,        // paper texture on the ground (0 = smooth)
  CLOUD_SHADOWS: 0.6,       // very slow drifting cloud shadows on the ground (0 = off)

  // ---------- Atmosphere ----------
  FOG_DENSITY: 0.0055,      // distant flakes dim into the night colour
  MIST_OPACITY: 0.22,       // soft lavender mist drifting around the screen edges

  // ---------- Camera / 3D depth ----------
  CAMERA_HEIGHT: 100,       // world units between the camera and the ground
  CAMERA_FOV: 55,           // wider = stronger perspective
  CAMERA_TILT: 5,           // degrees away from looking straight down
  BREATHING: 1.0,           // slow floating camera motion (0 = off)
  MOUSE_PARALLAX: 1.0,      // subtle mouse parallax (0 = off)
  ANIMATION_INTENSITY: 1.0, // master motion scale for everything that moves

  // ---------- Colours (matched to .login-page--aurora) ----------
  FOG_COLOR: "#221E55",        // mid-tone of your #0a0e27 → #2d1b69 gradient
  SNOW_COLOR: "#FFFFFF",
  SNOW_EDGE_COLOR: "#C4B5FD",  // lavender rim, echoes the violet aurora
  SHADOW_COLOR: "#070A1F",     // drift shadows on the ground
  PALETTE: {
    moonlight: "#E0E7FF",      // pale indigo snow light
    violet:    "#DDD6FE",
    blue:      "#BFDBFE",
    pink:      "#F9A8D4",      // faint echo of the pink aurora
  },

  // ---------- Depth layers (advanced) ----------
  // top / bottom: height as a fraction of CAMERA_HEIGHT (1 = at the camera, 0 = the ground)
  // size: world-unit range · speed: world units / second · ease > 1 = lingers near the camera
  LAYERS: {
    foreground: { count: 130,  size: [1.6, 3.6],   speed: 2.4, top: 0.97, bottom: 0.55, ease: 1.7,  wind: 0.30, sway: 0.35, opacity: 0.8,  baseBlur: 0.06, fadeTop: 0.12, fadeLen: 0.45 },
    midground:  { count: 1800, size: [0.55, 1.10], speed: 5.0, top: 0.80, bottom: 0.00, ease: 1.15, wind: 0.60, sway: 1.20, opacity: 0.9,  baseBlur: 0.00, fadeTop: 0.08, fadeLen: 0.12 },
    background: { count: 2600, size: [0.28, 0.50], speed: 5.5, top: 0.46, bottom: 0.00, ease: 1.0,  wind: 0.60, sway: 1.40, opacity: 0.8,  baseBlur: 0.08, fadeTop: 0.10, fadeLen: 0.15 },
  },

  // ---------- Performance ----------
  MAX_PIXEL_RATIO: 1.75,
  MOBILE_PIXEL_RATIO: 1.25,
  MOBILE_COUNT_SCALE: 0.55,
  ADAPTIVE_QUALITY: true,   // lowers resolution / particles if FPS drops
  PAUSE_WHEN_HIDDEN: true,
  REDUCED_MOTION: "gentle", // prefers-reduced-motion: 'gentle' (slow, no camera motion) or 'still'
};
/* ===================================================================== */

const TAU = Math.PI * 2;

/* ---------------------------------------------------------------------
   Utilities
   --------------------------------------------------------------------- */
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const rgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
};

function wash(ctx, x, y, r, col, a) {
  if (r <= 0) return;
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, `rgba(${col},${a})`);
  g.addColorStop(0.65, `rgba(${col},${a * 0.45})`);
  g.addColorStop(1, `rgba(${col},0)`);
  ctx.fillStyle = g;
  ctx.fillRect(x - r, y - r, r * 2, r * 2);
}

// Soft-edged stroke via the canvas shadow trick (works in every browser)
function softStroke(ctx, path, col, a, width, blur, W) {
  ctx.save();
  ctx.shadowColor = `rgba(${col},${a})`;
  ctx.shadowBlur = blur;
  ctx.shadowOffsetX = W * 3;
  ctx.translate(-W * 3, 0);
  ctx.strokeStyle = "#000";
  ctx.lineWidth = width;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.stroke(path);
  ctx.restore();
}

function irregularPath(cx, cy, r, rnd, wobble = 1) {
  const p = new Path2D();
  const n = 56;
  const ph = [rnd() * TAU, rnd() * TAU, rnd() * TAU];
  const amp = [(0.08 + rnd() * 0.06) * wobble, (0.05 + rnd() * 0.04) * wobble, 0.02 * wobble];
  for (let i = 0; i <= n; i++) {
    const a = (i / n) * TAU;
    const rr = r * (1 + amp[0] * Math.sin(a * 2 + ph[0]) + amp[1] * Math.sin(a * 3 + ph[1]) + amp[2] * Math.sin(a * 7 + ph[2]));
    const x = cx + Math.cos(a) * rr;
    const y = cy + Math.sin(a) * rr;
    if (i) p.lineTo(x, y); else p.moveTo(x, y);
  }
  p.closePath();
  return p;
}

function smoothPath(pts) {
  const p = new Path2D();
  p.moveTo(pts[0][0], pts[0][1]);
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
    p.bezierCurveTo(
      p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6,
      p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6,
      p2[0], p2[1]
    );
  }
  return p;
}

// Separable box blur on a single-channel float array (3 passes ≈ gaussian)
function blurChannel(c, w, h, r, passes, wrap) {
  const tmp = new Float32Array(Math.max(w, h));
  const inv = 1 / (2 * r + 1);
  const at = (i, n) => (wrap ? ((i % n) + n) % n : i < 0 ? 0 : i >= n ? n - 1 : i);
  for (let p = 0; p < passes; p++) {
    for (let y = 0; y < h; y++) {
      const o = y * w;
      let s = 0;
      for (let k = -r; k <= r; k++) s += c[o + at(k, w)];
      for (let x = 0; x < w; x++) { tmp[x] = s * inv; s += c[o + at(x + r + 1, w)] - c[o + at(x - r, w)]; }
      for (let x = 0; x < w; x++) c[o + x] = tmp[x];
    }
    for (let x = 0; x < w; x++) {
      let s = 0;
      for (let k = -r; k <= r; k++) s += c[at(k, h) * w + x];
      for (let y = 0; y < h; y++) { tmp[y] = s * inv; s += c[at(y + r + 1, h) * w + x] - c[at(y - r, h) * w + x]; }
      for (let y = 0; y < h; y++) c[y * w + x] = tmp[y];
    }
  }
}

// Blur an RGBA sub-rectangle of ImageData (premultiplied, so no dark fringes)
function blurRegion(img, W, x0, y0, w, h, r, passes) {
  const d = img.data;
  const n = w * h;
  const ch = [new Float32Array(n), new Float32Array(n), new Float32Array(n), new Float32Array(n)];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = ((y0 + y) * W + x0 + x) * 4, j = y * w + x, a = d[i + 3] / 255;
    ch[0][j] = d[i] * a; ch[1][j] = d[i + 1] * a; ch[2][j] = d[i + 2] * a; ch[3][j] = d[i + 3];
  }
  for (const c of ch) blurChannel(c, w, h, r, passes, false);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = ((y0 + y) * W + x0 + x) * 4, j = y * w + x, a = ch[3][j];
    const k = a > 0.01 ? 255 / a : 0;
    d[i] = ch[0][j] * k; d[i + 1] = ch[1][j] * k; d[i + 2] = ch[2][j] * k; d[i + 3] = a;
  }
}

/* ---------------------------------------------------------------------
   Procedural watercolour textures
   --------------------------------------------------------------------- */
function paintFlake(ctx, cx, cy, S, rnd, edge, ws) {
  const r = S * 0.27;
  const blobs = 6 + Math.floor(rnd() * 4);
  for (let i = 0; i < blobs; i++) {
    const a = rnd() * TAU, dist = r * 0.35 * Math.sqrt(rnd());
    const x = cx + Math.cos(a) * dist, y = cy + Math.sin(a) * dist;
    const br = r * (0.45 + rnd() * 0.4);
    const col = rnd() < 0.3 ? "245,243,255" : "255,255,255";
    wash(ctx, x, y, br, col, 0.32 + rnd() * 0.2);
  }
  const shape = irregularPath(cx, cy, r * 0.8, rnd);
  ctx.fillStyle = "rgba(255,255,255,0.22)";
  ctx.fill(shape);

  ctx.lineJoin = "round";
  ctx.strokeStyle = `rgba(${edge},${0.28 * ws})`;
  ctx.lineWidth = r * 0.06;
  ctx.stroke(shape);
  const shape2 = irregularPath(cx + (rnd() - 0.5) * r * 0.1, cy + (rnd() - 0.5) * r * 0.1, r * 0.86, rnd);
  ctx.strokeStyle = `rgba(${edge},${0.12 * ws})`;
  ctx.lineWidth = r * 0.14;
  ctx.stroke(shape2);

  for (let i = 0; i < 140; i++) {
    const a = rnd() * TAU, d = r * 0.95 * Math.sqrt(rnd());
    ctx.fillStyle = rnd() < 0.5 ? `rgba(${edge},${0.08 * ws})` : "rgba(255,255,255,0.35)";
    ctx.beginPath();
    ctx.arc(cx + Math.cos(a) * d, cy + Math.sin(a) * d, 0.6 + rnd() * 1.6, 0, TAU);
    ctx.fill();
  }

  ctx.globalCompositeOperation = "destination-out";
  for (let i = 0; i < 3; i++) {
    const a = rnd() * TAU, d = r * 0.5 * rnd();
    wash(ctx, cx + Math.cos(a) * d, cy + Math.sin(a) * d, r * (0.15 + rnd() * 0.2), "0,0,0", 0.28);
  }
  ctx.globalCompositeOperation = "source-over";
}

function hexPath(cx, cy, r, rot) {
  const p = new Path2D();
  for (let i = 0; i <= 6; i++) {
    const a = rot + (i / 6) * TAU;
    const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
    if (i) p.lineTo(x, y); else p.moveTo(x, y);
  }
  p.closePath();
  return p;
}

// Six-armed ice crystal, painted like watercolour: lavender pigment bleed,
// white body, bright core. Each arm is hand-jittered so no two look machine-made.
// type 0 = stellar dendrite · 1 = fern · 2 = plate with paddle tips · 3 = sectored star
function paintCrystal(ctx, cx, cy, S, rnd, edge, ws, type) {
  const R = S * 0.42;
  const w = S * 0.016;
  const rot0 = rnd() * TAU;
  const lines = new Path2D();
  const plates = new Path2D();

  const seg = (a, x1, y1, x2, y2) => {
    const c = Math.cos(a), s = Math.sin(a);
    lines.moveTo(cx + x1 * c - y1 * s, cy + x1 * s + y1 * c);
    lines.lineTo(cx + x2 * c - y2 * s, cy + x2 * s + y2 * c);
  };

  // [position along the arm, branch length, branch angle]
  const branches = {
    0: [[0.34, 0.3, 1.05], [0.56, 0.24, 1.05], [0.76, 0.15, 1.05]],
    1: [0.22, 0.32, 0.42, 0.52, 0.62, 0.72, 0.82, 0.9].map((p, i) => [p, 0.2 - i * 0.018, 0.9]),
    2: [[0.6, 0.2, 1.05]],
    3: [[0.45, 0.34, 1.05], [0.7, 0.18, 1.05]],
  }[type];
  const inner = type === 2 ? 0.26 : 0.05;
  const armLen = [1, 0.95, 0.8, 0.98][type];

  for (let i = 0; i < 6; i++) {
    const a = rot0 + (i / 6) * TAU + (rnd() - 0.5) * 0.03;
    const L = R * armLen * (0.94 + rnd() * 0.1);
    seg(a, R * inner, 0, L, 0);
    for (const [p, l, ang] of branches) {
      const bx = L * p, bl = R * l * (0.88 + rnd() * 0.24);
      seg(a, bx, 0, bx + Math.cos(ang) * bl, Math.sin(ang) * bl);
      seg(a, bx, 0, bx + Math.cos(ang) * bl, -Math.sin(ang) * bl);
    }
    if (type === 2) plates.addPath(hexPath(cx + Math.cos(a) * L, cy + Math.sin(a) * L, R * 0.09, a));
  }
  if (type === 2) plates.addPath(hexPath(cx, cy, R * 0.28, rot0 + TAU / 12));
  if (type === 3) lines.addPath(hexPath(cx, cy, R * 0.15, rot0 + TAU / 12));

  // soft halo so the crystal sits in a little glow of wet paint
  wash(ctx, cx, cy, R * 0.8, "255,255,255", 0.1);

  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // 1) pigment bleed around the shape
  ctx.strokeStyle = `rgba(${edge},${0.32 * ws})`;
  ctx.lineWidth = w * 3.2;
  ctx.stroke(lines);
  ctx.stroke(plates);
  ctx.fillStyle = `rgba(${edge},${0.12 * ws})`;
  ctx.fill(plates);

  // 2) white body
  ctx.fillStyle = "rgba(255,255,255,0.4)";
  ctx.fill(plates);
  ctx.strokeStyle = "rgba(255,255,255,0.78)";
  ctx.lineWidth = w * 1.6;
  ctx.stroke(lines);
  ctx.stroke(plates);

  // 3) bright core
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.lineWidth = w * 0.7;
  ctx.stroke(lines);

  // centre and a few granulation specks
  wash(ctx, cx, cy, R * 0.12, "255,255,255", 0.85);
  for (let i = 0; i < 40; i++) {
    const a = rnd() * TAU, d = R * rnd();
    ctx.fillStyle = `rgba(${edge},${0.1 * ws})`;
    ctx.beginPath();
    ctx.arc(cx + Math.cos(a) * d, cy + Math.sin(a) * d, 0.6 + rnd() * 1.2, 0, TAU);
    ctx.fill();
  }
}

// 4×2 atlas: row 0 = in-focus flakes, row 1 = the same flakes out of focus
function makeFlakeAtlas() {
  const S = 256, cols = 4, W = S * cols, Hh = S * 2;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = Hh;
  const ctx = cv.getContext("2d", { willReadFrequently: true });
  const rnd = mulberry32(7);
  const edge = rgb(CONFIG.SNOW_EDGE_COLOR);
  const ws = Math.max(0.3, CONFIG.WATERCOLOR_STRENGTH);
  const style = CONFIG.FLAKE_STYLE;
  const isCrystal = [0, 1, 2, 3].map((i) => style === "crystal" || (style === "mixed" && i < 2));
  for (let i = 0; i < cols; i++) {
    if (isCrystal[i]) paintCrystal(ctx, i * S + S / 2, S / 2, S, rnd, edge, ws, style === "mixed" ? i * 2 : i);
    else paintFlake(ctx, i * S + S / 2, S / 2, S, rnd, edge, ws);
  }

  const img = ctx.getImageData(0, 0, W, Hh);
  img.data.copyWithin(W * S * 4, 0, W * S * 4);
  for (let i = 0; i < cols; i++) {
    blurRegion(img, W, i * S, 0, S, S, isCrystal[i] ? 1 : 2, 2);   // soften edges, keep crystal detail
    blurRegion(img, W, i * S, S, S, S, isCrystal[i] ? 6 : 12, 3);  // defocused version
  }
  ctx.putImageData(img, 0, 0);

  const tex = new THREE.CanvasTexture(cv);
  tex.flipY = false;
  tex.premultiplyAlpha = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  return tex;
}

// Transparent night snowfield: moonlit washes, drift shadows and soft blooms.
// Painted with alpha so your gradient shows through.
function makeFloorWash(maxAniso, isMobile) {
  const W = isMobile ? 1024 : 1536;
  const cv = document.createElement("canvas");
  cv.width = cv.height = W;
  const ctx = cv.getContext("2d");
  const rnd = mulberry32(21);
  const ws = CONFIG.WATERCOLOR_STRENGTH;
  const P = CONFIG.PALETTE;
  const moon = rgb(P.moonlight), violet = rgb(P.violet), blue = rgb(P.blue), pink = rgb(P.pink);
  const shadow = rgb(CONFIG.SHADOW_COLOR);

  // moonlit washes
  for (let i = 0; i < 24; i++) {
    const k = rnd();
    const [col, a] = k < 0.45 ? [moon, 0.05] : k < 0.72 ? [violet, 0.045] : k < 0.92 ? [blue, 0.04] : [pink, 0.035];
    wash(ctx, rnd() * W, rnd() * W, W * (0.12 + rnd() * 0.3), col, a * ws);
  }

  // elongated drift shadows
  for (let i = 0; i < 16; i++) {
    ctx.save();
    ctx.translate(rnd() * W, rnd() * W);
    ctx.rotate(-0.4 + rnd() * 0.5);
    ctx.scale(1, 0.3 + rnd() * 0.3);
    wash(ctx, 0, 0, W * (0.06 + rnd() * 0.12), shadow, 0.2 * ws);
    ctx.restore();
  }

  // loose brush sweeps of moonlight
  for (let i = 0; i < 7; i++) {
    const y0 = rnd() * W, pts = [];
    for (let k = 0; k <= 5; k++) pts.push([-W * 0.1 + (W * 1.2 * k) / 5, y0 + (rnd() - 0.5) * W * 0.08]);
    softStroke(ctx, smoothPath(pts), moon, 0.07 * ws, W * (0.01 + rnd() * 0.03), W * 0.02, W);
  }

  // pigment blooms
  for (let i = 0; i < 28; i++) {
    const p = irregularPath(rnd() * W, rnd() * W, W * (0.012 + rnd() * 0.05), rnd, 1.6);
    softStroke(ctx, p, violet, 0.035 * ws, 2, W * 0.005, W);
  }

  const tex = new THREE.CanvasTexture(cv);
  tex.premultiplyAlpha = true;
  tex.wrapS = tex.wrapT = THREE.MirroredRepeatWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.anisotropy = Math.min(8, maxAniso);
  return tex;
}

function makeGrain() {
  const N = 256, rnd = mulberry32(9);
  const a = new Float32Array(N * N), b = new Float32Array(N * N);
  for (let i = 0; i < N * N; i++) { a[i] = rnd(); b[i] = rnd(); }
  blurChannel(a, N, N, 3, 2, true);
  blurChannel(b, N, N, 1, 1, true);
  let mn = 1e9, mx = -1e9;
  const v = new Float32Array(N * N);
  for (let i = 0; i < N * N; i++) { v[i] = a[i] * 0.7 + b[i] * 0.3; mn = Math.min(mn, v[i]); mx = Math.max(mx, v[i]); }
  const data = new Uint8Array(N * N * 4);
  for (let i = 0; i < N * N; i++) {
    const g = ((v[i] - mn) / (mx - mn)) * 255;
    data[i * 4] = data[i * 4 + 1] = data[i * 4 + 2] = g;
    data[i * 4 + 3] = 255;
  }
  const tex = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

function makeMistTexture(seed, isMobile) {
  const S = isMobile ? 256 : 512;
  const cv = document.createElement("canvas");
  cv.width = cv.height = S;
  const ctx = cv.getContext("2d");
  const rnd = mulberry32(seed);
  const P = CONFIG.PALETTE;
  const cols = [rgb(P.moonlight), rgb(P.violet), rgb(P.blue)];
  for (let i = 0; i < 44; i++) {
    const a = rnd() * TAU, d = S * 0.2 * Math.sqrt(rnd());
    const x = S / 2 + Math.cos(a) * d, y = S / 2 + Math.sin(a) * d * 0.8;
    const maxR = S * 0.49 - Math.hypot(x - S / 2, y - S / 2);
    const col = rnd() < 0.5 ? cols[0] : rnd() < 0.6 ? cols[1] : cols[2];
    wash(ctx, x, y, Math.min(S * (0.08 + rnd() * 0.16), maxR), col, 0.06 + rnd() * 0.07);
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.premultiplyAlpha = true;
  return tex;
}

/* ---------------------------------------------------------------------
   Shaders (all output premultiplied alpha onto a transparent canvas)
   --------------------------------------------------------------------- */
const SNOW_VS = /* glsl */ `
  attribute vec4 aParams; // size, variant, spin, seed   (position.xyz = random seeds)
  uniform float uTime, uTop, uBottom, uSpeed, uEase;
  uniform float uHalfW, uHalfD, uCenterZ, uSway, uSizeMul;
  uniform vec2  uWind, uGustOffset;
  uniform float uGust;
  uniform float uPixelScale, uMaxPoint, uFocusNear, uFocusFar, uBaseBlur;
  uniform float uFogDensity, uFadeTop, uFadeLen, uTumble, uDpr, uDotFrom, uDotTo;
  uniform float uSparkleRate, uSparkleSpeed;
  varying float vAlpha, vBlur, vRot, vVariant, vHaze, vTilt, vDot, vGlint;

  float hash11(float n) { return fract(sin(n) * 43758.5453123); }

  void main() {
    vec3 s = position;
    float sw = aParams.w;
    float range = uTop - uBottom;
    float duration = (range / uSpeed) * mix(0.8, 1.25, sw);

    // closed-form looping fall: every flake is somewhere in its own cycle
    float tt = uTime / duration + s.z;
    float cycle = floor(tt);
    float p = tt - cycle;
    float y = uTop - pow(p, uEase) * range;

    // new random column every cycle, so paths never visibly repeat
    float x = (hash11(cycle * 17.13 + s.x * 91.7 + 3.1) * 2.0 - 1.0) * uHalfW;
    float z = uCenterZ + (hash11(cycle * 29.71 + s.y * 57.3 + 7.9) * 2.0 - 1.0) * uHalfD;

    // steady wind across this flake's own fall
    float fallSec = p * duration - duration * 0.5;
    x += uWind.x * fallSec;
    z += uWind.y * fallSec;

    // sine-wave flutter (livelier during a gust)
    float ph = s.x * 6.2831 + s.y * 3.1;
    float fr = mix(0.7, 1.5, s.y);
    float amp = uSway * mix(0.5, 1.2, sw) * (1.0 + uGust * 1.5);
    x += sin(uTime * fr + ph) * amp;
    z += cos(uTime * fr * 0.83 + ph * 1.7) * amp * 0.7;
    x += sin(uTime * 0.06 + y * 0.035 + s.z * 2.0) * uSway * 0.9;
    z += sin(uTime * 0.045 + y * 0.028 + s.x * 3.0) * uSway * 0.5;

    // sign-in gust, then wrap inside the spawn area so coverage never thins out
    x += uGustOffset.x * mix(0.8, 1.2, sw);
    z += uGustOffset.y * mix(0.8, 1.2, sw);
    x = mod(x + uHalfW, 2.0 * uHalfW) - uHalfW;
    z = uCenterZ + mod(z - uCenterZ + uHalfD, 2.0 * uHalfD) - uHalfD;

    vec4 mv = modelViewMatrix * vec4(x, y, z, 1.0);
    float depth = -mv.z;
    gl_Position = projectionMatrix * mv;

    float px = aParams.x * uSizeMul * uPixelScale / max(depth, 0.01);
    gl_PointSize = clamp(px, 1.5, uMaxPoint);

    float fadeIn   = smoothstep(0.0, uFadeTop, p);
    float fadeOut  = smoothstep(uBottom, uBottom + uFadeLen, y);
    float nearFade = smoothstep(1.2, 4.5, depth);
    float tiny     = clamp(px / 1.5, 0.0, 1.0);

    vBlur = clamp(max(1.0 - smoothstep(uFocusNear, uFocusFar, depth), uBaseBlur), 0.0, 1.0);
    float hz = depth * uFogDensity;
    vHaze = 1.0 - exp(-hz * hz);
    vAlpha = fadeIn * fadeOut * nearFade * tiny * mix(0.6, 1.0, sw);
    vRot = s.y * 6.2831 + uTime * aParams.z;
    vVariant = aParams.y;

    // slow tumble: the flake narrows as it tilts edge-on, then opens again
    vTilt = mix(1.0, abs(cos(uTime * mix(0.25, 0.8, s.x) + s.z * 6.2831)), uTumble);

    // small on screen = soft round speck, large = full crystal
    vDot = 1.0 - smoothstep(uDotFrom, uDotTo, px / uDpr);

    // occasional glint: a short pulse, and only for a few flakes each cycle
    float ph2 = uTime * uSparkleSpeed * mix(0.7, 1.3, s.y) + s.z * 13.0;
    float cyc2 = floor(ph2);
    float f2 = fract(ph2) - 0.5;
    float gate = step(1.0 - uSparkleRate, hash11(cyc2 * 7.31 + s.x * 19.7 + sw * 3.3));
    vGlint = gate * exp(-f2 * f2 * 160.0);
  }
`;

const SNOW_FS = /* glsl */ `
  uniform sampler2D uAtlas;
  uniform vec3 uColor, uFogColor, uTintTop, uTintMid, uTintBottom;
  uniform float uOpacity, uTint, uSparkle;
  uniform vec2 uResolution;
  varying float vAlpha, vBlur, vRot, vVariant, vHaze, vTilt, vDot, vGlint;

  void main() {
    if (vAlpha < 0.002) discard;
    vec2 c0 = gl_PointCoord - 0.5;
    float sn = sin(vRot), cs = cos(vRot);
    vec2 c = mat2(cs, -sn, sn, cs) * c0;
    c.x /= max(vTilt, 0.15);

    // crystal from the atlas (sampled unconditionally so mipmaps stay valid)
    vec2 uv = clamp(c + 0.5, 0.0, 1.0) * 0.96 + 0.02;
    float col = floor(vVariant + 0.5);
    vec4 sharp = texture2D(uAtlas, (vec2(col, 0.0) + uv) * vec2(0.25, 0.5));
    vec4 soft  = texture2D(uAtlas, (vec2(col, 1.0) + uv) * vec2(0.25, 0.5));
    vec4 t = mix(sharp, soft, vBlur) * step(max(abs(c.x), abs(c.y)), 0.5);

    // distant flakes: soft round speck instead of a tiny crystal
    float dotA = pow(clamp(1.0 - length(c0) / 0.3, 0.0, 1.0), 1.6) * 0.95;
    t = mix(t, vec4(dotA), vDot);

    // aurora light: blue up high, violet in the middle, pink low down
    vec2 sp = gl_FragCoord.xy / uResolution;
    float v = sp.y + (sp.x - 0.5) * 0.15;
    vec3 tint = mix(uTintBottom, uTintMid, smoothstep(0.05, 0.5, v));
    tint = mix(tint, uTintTop, smoothstep(0.5, 0.95, v));
    vec3 snow = mix(uColor, tint, uTint);

    float k = vAlpha * uOpacity * (1.0 - vHaze * 0.6) * (1.0 - vBlur * 0.3);
    vec3 rgbPm = mix(t.rgb * snow, uFogColor * t.a, vHaze * 0.6);
    vec4 outc = vec4(rgbPm * k, t.a * k);

    // glint: soft four-point star added as light
    float g = vGlint * uSparkle * vAlpha * (1.0 - vHaze);
    if (g > 0.001) {
      float cross = exp(-abs(c0.x) * 60.0) * exp(-abs(c0.y) * 7.0)
                  + exp(-abs(c0.y) * 60.0) * exp(-abs(c0.x) * 7.0);
      float core = exp(-dot(c0, c0) * 90.0);
      float glow = (cross * 0.7 + core) * g;
      outc.rgb += mix(vec3(1.0), tint, 0.3) * glow;
      outc.a += glow * 0.25;
    }

    if (outc.a < 0.002) discard;
    gl_FragColor = outc;
  }
`;

const FLOOR_VS = /* glsl */ `
  attribute float aShade;
  varying vec2 vUv, vWorld;
  varying float vShade, vDepth;
  void main() {
    vUv = uv;
    vWorld = position.xz;
    vShade = aShade;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vDepth = -mv.z;
    gl_Position = projectionMatrix * mv;
  }
`;

const FLOOR_FS = /* glsl */ `
  uniform sampler2D uWash, uGrain;
  uniform float uWashScale, uGrainScale, uGrainStrength, uTime, uCloud, uFogDensity;
  uniform vec3 uShadowColor;
  varying vec2 vUv, vWorld;
  varying float vShade, vDepth;

  float hash21(vec2 p) { p = fract(p * vec2(233.34, 851.73)); p += dot(p, p + 23.45); return fract(p.x * p.y); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p), u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash21(i), hash21(i + vec2(1.0, 0.0)), u.x),
               mix(hash21(i + vec2(0.0, 1.0)), hash21(i + vec2(1.0, 1.0)), u.x), u.y);
  }
  float fbm(vec2 p) {
    float v = 0.0, a = 0.5;
    for (int i = 0; i < 3; i++) { v += a * vnoise(p); p = p * 2.02 + vec2(1.7, 9.2); a *= 0.5; }
    return v;
  }

  void main() {
    vec4 col = texture2D(uWash, (vUv - 0.5) * uWashScale + 0.5);   // premultiplied washes

    // paper grain: gently modulates the washes
    float g1 = texture2D(uGrain, vWorld * uGrainScale).r;
    float g2 = texture2D(uGrain, vWorld * uGrainScale * 2.63 + 0.31).r;
    float grain = (g1 - 0.5) * 0.7 + (g2 - 0.5) * 0.5;
    col *= 1.0 + grain * uGrainStrength * 3.0;

    // drift shading + extremely slow cloud shadows, as a darkening layer
    float n = fbm(vWorld * 0.011 + vec2(uTime * 0.003, uTime * 0.002));
    float sh = clamp((1.0 - vShade) * 1.6 + smoothstep(0.5, 0.78, n) * uCloud * 0.16, 0.0, 0.5);
    col = col * (1.0 - sh) + vec4(uShadowColor * sh, sh);

    // haze
    float hz = vDepth * uFogDensity;
    col *= 1.0 - (1.0 - exp(-hz * hz)) * 0.5;

    gl_FragColor = col;
  }
`;

const MIST_VS = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;

const MIST_FS = /* glsl */ `
  uniform sampler2D uMap;
  uniform float uOpacity;
  uniform vec3 uFogColor;
  varying vec2 vUv;
  void main() {
    vec4 t = texture2D(uMap, vUv);
    vec3 c = mix(t.rgb, uFogColor * t.a, 0.2);
    gl_FragColor = vec4(c, t.a) * uOpacity;
  }
`;

/* ---------------------------------------------------------------------
   Scene setup — returns a cleanup function
   --------------------------------------------------------------------- */
function createSnowScene(container) {
  THREE.ColorManagement.enabled = false; // colours stay exactly as authored

  const H = CONFIG.CAMERA_HEIGHT;
  const isMobile = window.matchMedia("(pointer: coarse)").matches || Math.min(window.screen.width, window.screen.height) < 700;
  const reducedMQ = window.matchMedia("(prefers-reduced-motion: reduce)");
  let reducedMotion = reducedMQ.matches;

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true, premultipliedAlpha: true, powerPreference: "high-performance" });
  } catch (err) {
    console.warn("WebGL unavailable — login keeps its CSS background.", err);
    return { dispose() {}, gust() {} };
  }
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  renderer.setClearColor(0x000000, 0);
  let dpr = Math.min(window.devicePixelRatio || 1, isMobile ? CONFIG.MOBILE_PIXEL_RATIO : CONFIG.MAX_PIXEL_RATIO);
  renderer.setPixelRatio(dpr);
  renderer.domElement.setAttribute("aria-hidden", "true");
  container.appendChild(renderer.domElement);

  const gl = renderer.getContext();
  const maxPointHW = gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE)[1] || 64;

  const scene = new THREE.Scene();
  const fogColor = new THREE.Color(CONFIG.FOG_COLOR);
  const disposables = [];

  const camera = new THREE.PerspectiveCamera(CONFIG.CAMERA_FOV, 1, 0.3, H * 4);
  camera.up.set(0, 0, -1);
  const tanV = Math.tan(THREE.MathUtils.degToRad(CONFIG.CAMERA_FOV / 2));
  const tilt = Math.tan(THREE.MathUtils.degToRad(CONFIG.CAMERA_TILT));
  const lookTarget = new THREE.Vector3(0, 0, -H * tilt);
  camera.position.set(0, H, 0);
  camera.lookAt(lookTarget);

  /* ----- Ground ----- */
  const FLOOR_SIZE = H * 6;
  const floorGeo = new THREE.PlaneGeometry(FLOOR_SIZE, FLOOR_SIZE, isMobile ? 64 : 96, isMobile ? 64 : 96);
  floorGeo.rotateX(-Math.PI / 2);
  {
    const pos = floorGeo.attributes.position;
    const shade = new Float32Array(pos.count);
    const hgt = (x, z) =>
      1.4 * Math.sin(x * 0.031 + 1.3) * Math.cos(z * 0.027 + 0.4) +
      0.8 * Math.sin(x * 0.07 - z * 0.052 + 2.1) +
      0.35 * Math.sin(z * 0.13 + x * 0.021);
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), z = pos.getZ(i), e = 0.5;
      pos.setY(i, hgt(x, z));
      const dx = (hgt(x + e, z) - hgt(x - e, z)) / (2 * e);
      const dz = (hgt(x, z + e) - hgt(x, z - e)) / (2 * e);
      shade[i] = THREE.MathUtils.clamp(0.965 + (dx * 0.7 - dz * 0.5) * 0.9, 0.9, 1.0);
    }
    floorGeo.setAttribute("aShade", new THREE.BufferAttribute(shade, 1));
  }
  const washTex = makeFloorWash(renderer.capabilities.getMaxAnisotropy(), isMobile);
  const grainTex = makeGrain();
  const floorMat = new THREE.ShaderMaterial({
    uniforms: {
      uWash: { value: washTex },
      uGrain: { value: grainTex },
      uWashScale: { value: FLOOR_SIZE / (H * 2) },
      uGrainScale: { value: 1 / 13 },
      uGrainStrength: { value: 0.1 * CONFIG.PAPER_GRAIN },
      uTime: { value: 0 },
      uCloud: { value: CONFIG.CLOUD_SHADOWS },
      uFogDensity: { value: CONFIG.FOG_DENSITY },
      uShadowColor: { value: new THREE.Color(CONFIG.SHADOW_COLOR) },
    },
    vertexShader: FLOOR_VS,
    fragmentShader: FLOOR_FS,
    transparent: true,
    depthWrite: false,
    premultipliedAlpha: true,
  });
  const floor = new THREE.Mesh(floorGeo, floorMat);
  floor.renderOrder = 0;
  scene.add(floor);
  disposables.push(floorGeo, floorMat, washTex, grainTex);

  /* ----- Snow layers ----- */
  const atlas = makeFlakeAtlas();
  disposables.push(atlas);
  const countScale = CONFIG.SNOW_COUNT * (isMobile ? CONFIG.MOBILE_COUNT_SCALE : 1);
  const layerOrder = { background: 1, midground: 2, foreground: 4 };
  const gustOffset = new THREE.Vector2();           // shared by all layers
  const snowResolution = new THREE.Vector2(1, 1);   // shared by all layers
  const tintTop = new THREE.Color(CONFIG.TINT_TOP);
  const tintMid = new THREE.Color(CONFIG.TINT_MID);
  const tintBottom = new THREE.Color(CONFIG.TINT_BOTTOM);
  const layers = [];

  for (const [name, def] of Object.entries(CONFIG.LAYERS)) {
    const count = Math.max(1, Math.round(def.count * countScale));
    const rnd = mulberry32(name.length * 131 + 17);
    const seeds = new Float32Array(count * 3);
    const params = new Float32Array(count * 4);
    for (let i = 0; i < count; i++) {
      seeds[i * 3] = rnd(); seeds[i * 3 + 1] = rnd(); seeds[i * 3 + 2] = rnd();
      params[i * 4] = def.size[0] + (def.size[1] - def.size[0]) * Math.pow(rnd(), 1.6);
      params[i * 4 + 1] = Math.floor(rnd() * 4);
      params[i * 4 + 2] = (rnd() - 0.5) * 0.6;
      params[i * 4 + 3] = rnd();
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(seeds, 3));
    geo.setAttribute("aParams", new THREE.BufferAttribute(params, 4));

    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 }, uTop: { value: 0 }, uBottom: { value: 0 }, uSpeed: { value: 1 }, uEase: { value: 1 },
        uHalfW: { value: 1 }, uHalfD: { value: 1 }, uCenterZ: { value: 0 }, uSway: { value: 0 }, uSizeMul: { value: 1 },
        uWind: { value: new THREE.Vector2() }, uPixelScale: { value: 1 }, uMaxPoint: { value: 64 },
        uFocusNear: { value: 5 }, uFocusFar: { value: 22 }, uBaseBlur: { value: 0 },
        uFogDensity: { value: CONFIG.FOG_DENSITY }, uFadeTop: { value: 0.1 }, uFadeLen: { value: 10 }, uTumble: { value: 0 },
        uGustOffset: { value: gustOffset }, uGust: { value: 0 },
        uDpr: { value: 1 }, uDotFrom: { value: 6 }, uDotTo: { value: 14 },
        uSparkleRate: { value: CONFIG.SPARKLE_RATE }, uSparkleSpeed: { value: CONFIG.SPARKLE_SPEED },
        uSparkle: { value: CONFIG.SPARKLE_STRENGTH }, uTint: { value: CONFIG.AURORA_TINT },
        uTintTop: { value: tintTop }, uTintMid: { value: tintMid }, uTintBottom: { value: tintBottom },
        uResolution: { value: snowResolution },
        uAtlas: { value: atlas }, uColor: { value: new THREE.Color(CONFIG.SNOW_COLOR) },
        uFogColor: { value: fogColor }, uOpacity: { value: 1 },
      },
      vertexShader: SNOW_VS,
      fragmentShader: SNOW_FS,
      transparent: true,
      depthWrite: false,
      premultipliedAlpha: true,
    });
    const points = new THREE.Points(geo, mat);
    points.frustumCulled = false;
    points.renderOrder = layerOrder[name] ?? 2;
    scene.add(points);
    layers.push({ name, def, count, geo, mat });
    disposables.push(geo, mat);
  }

  function updateLayer(L, pixelScale, maxPt) {
    const d = L.def, u = L.mat.uniforms;
    const topY = d.top * H, botY = d.bottom * H;
    const depthAtBottom = H - botY;
    const dur = ((topY - botY) / d.speed) * 1.25;
    const ang = THREE.MathUtils.degToRad(CONFIG.WIND_DIRECTION);
    const windSpeed = CONFIG.WIND_STRENGTH * d.wind;
    const wx = Math.cos(ang) * windSpeed, wz = Math.sin(ang) * windSpeed;
    const sway = d.sway * CONFIG.SWAY_AMOUNT;

    u.uTop.value = topY;
    u.uBottom.value = botY;
    u.uSpeed.value = d.speed;
    u.uEase.value = d.ease;
    u.uHalfW.value = depthAtBottom * tanV * camera.aspect * 1.18 + sway * 2 + Math.abs(wx) * dur * 0.5 + 3;
    u.uHalfD.value = depthAtBottom * tanV * 1.18 + sway * 2 + Math.abs(wz) * dur * 0.5 + 3;
    u.uCenterZ.value = -depthAtBottom * tilt;
    u.uWind.value.set(wx, wz);
    u.uSway.value = sway;
    u.uSizeMul.value = L.name === "foreground" ? CONFIG.FOREGROUND_SIZE : 1;
    u.uPixelScale.value = pixelScale;
    u.uMaxPoint.value = maxPt;
    u.uFocusNear.value = 5;
    u.uFocusFar.value = 5 + 17 * CONFIG.SNOW_SOFTNESS;
    u.uBaseBlur.value = Math.min(1, d.baseBlur * CONFIG.SNOW_SOFTNESS);
    u.uFadeTop.value = d.fadeTop;
    u.uFadeLen.value = d.fadeLen * (topY - botY);
    u.uOpacity.value = d.opacity * CONFIG.SNOW_OPACITY;
    u.uTumble.value = THREE.MathUtils.clamp(CONFIG.TUMBLE_AMOUNT, 0, 1);
    u.uDpr.value = renderer.getPixelRatio();
    u.uDotFrom.value = CONFIG.DISTANT_DOTS ? 6 : 0;
    u.uDotTo.value = CONFIG.DISTANT_DOTS ? 14 : 0.001;
  }

  /* ----- Edge mist ----- */
  const mistTextures = [makeMistTexture(101, isMobile), makeMistTexture(202, isMobile), makeMistTexture(303, isMobile)];
  const mistGeo = new THREE.PlaneGeometry(1, 1);
  mistGeo.rotateX(-Math.PI / 2);
  disposables.push(mistGeo, ...mistTextures);
  const mists = [];
  const mRnd = mulberry32(55);
  const MIST_COUNT = isMobile ? 5 : 7;
  for (let i = 0; i < MIST_COUNT; i++) {
    const mat = new THREE.ShaderMaterial({
      uniforms: { uMap: { value: mistTextures[i % 3] }, uOpacity: { value: 0 }, uFogColor: { value: fogColor } },
      vertexShader: MIST_VS,
      fragmentShader: MIST_FS,
      transparent: true,
      depthWrite: false,
      premultipliedAlpha: true,
    });
    disposables.push(mat);
    const mesh = new THREE.Mesh(mistGeo, mat);
    mesh.renderOrder = 3;
    mesh.frustumCulled = false;
    scene.add(mesh);
    mists.push({
      mesh,
      depth: H * (0.2 + mRnd() * 0.3),
      a0: (i / MIST_COUNT) * TAU + mRnd() * 0.6,
      speed: (mRnd() < 0.5 ? -1 : 1) * (0.002 + mRnd() * 0.003),
      rf: 0.95 + mRnd() * 0.25,
      size: 0.75 + mRnd() * 0.5,
      r0: mRnd() * TAU,
      rs: (mRnd() - 0.5) * 0.004,
      op: 0.6 + mRnd() * 0.4,
      ph: mRnd() * TAU,
    });
  }
  function updateMist(t) {
    for (const c of mists) {
      const hx = c.depth * tanV * camera.aspect, hz = c.depth * tanV;
      const a = c.a0 + t * c.speed;
      c.mesh.position.set(Math.cos(a) * hx * c.rf, H - c.depth, -c.depth * tilt + Math.sin(a) * hz * c.rf);
      c.mesh.rotation.y = c.r0 + t * c.rs;
      const s = hz * 2 * c.size * (1 + 0.05 * Math.sin(t * 0.05 + c.ph));
      c.mesh.scale.set(s, 1, s);
      c.mesh.material.uniforms.uOpacity.value = CONFIG.MIST_OPACITY * c.op * (0.85 + 0.15 * Math.sin(t * 0.03 + c.ph));
    }
  }

  /* ----- Animation ----- */
  let simTime = 0, wallTime = 0, last = 0, rafId = null;
  let gustT = -1;
  function triggerGust() {
    if (reducedMotion || CONFIG.GUST_STRENGTH <= 0 || rafId === null) return;
    gustT = 0;
  }
  function updateGust(dt) {
    let env = 0;
    if (gustT >= 0) {
      gustT += dt;
      env = gustT < 0.4 ? THREE.MathUtils.smoothstep(gustT, 0, 0.4) : Math.exp(-(gustT - 0.4) / 0.9);
      const ang = THREE.MathUtils.degToRad(CONFIG.WIND_DIRECTION);
      gustOffset.x += Math.cos(ang) * env * CONFIG.GUST_STRENGTH * dt;
      gustOffset.y += Math.sin(ang) * env * CONFIG.GUST_STRENGTH * dt;
      if (gustT > 5) { gustT = -1; env = 0; }
    }
    for (const L of layers) L.mat.uniforms.uGust.value = env;
  }
  const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
  const stillMode = () => reducedMotion && CONFIG.REDUCED_MOTION === "still";

  function step(dt) {
    const m = (reducedMotion ? 0.3 : 1) * CONFIG.ANIMATION_INTENSITY;
    simTime += dt * CONFIG.SNOW_SPEED * m;
    wallTime += dt * m;

    const camMotion = reducedMotion ? 0 : 1;
    const k = 1 - Math.exp(-dt * 1.2);
    mouse.x += (mouse.tx * CONFIG.MOUSE_PARALLAX * camMotion - mouse.x) * k;
    mouse.y += (mouse.ty * CONFIG.MOUSE_PARALLAX * camMotion - mouse.y) * k;

    const b = CONFIG.BREATHING * camMotion;
    camera.position.set(
      Math.sin(wallTime * 0.071) * 1.2 * b + mouse.x * 2.2,
      H + Math.sin(wallTime * 0.42) * 0.7 * b,
      Math.cos(wallTime * 0.053) * 0.9 * b + mouse.y * 1.6
    );
    camera.lookAt(lookTarget);

    updateMist(wallTime);
    updateGust(dt);
    for (const L of layers) L.mat.uniforms.uTime.value = simTime;
    floorMat.uniforms.uTime.value = wallTime;
    renderer.render(scene, camera);
  }

  let perfT = 0, perfN = 0, warmup = 3, qStep = 0;
  function adaptive(raw) {
    if (!CONFIG.ADAPTIVE_QUALITY || qStep >= 3) return;
    if (warmup > 0) { warmup -= raw; return; }
    if (raw > 0.25) return;
    perfT += raw; perfN++;
    if (perfT < 2) return;
    const avg = perfT / perfN;
    perfT = 0; perfN = 0;
    if (avg > 1 / 42) {
      qStep++;
      if (dpr > 1) {
        dpr = Math.max(1, dpr - 0.25);
        renderer.setPixelRatio(dpr);
        resize();
      } else {
        for (const L of layers) L.geo.setDrawRange(0, Math.floor(L.count * Math.pow(0.7, qStep)));
      }
    }
  }

  function frame(now) {
    rafId = requestAnimationFrame(frame);
    const raw = Math.max(0, (now - last) / 1000);
    last = now;
    step(Math.min(raw, 1 / 20));
    adaptive(raw);
  }
  function start() {
    if (rafId !== null) return;
    if (stillMode()) { step(0); return; }
    last = performance.now();
    rafId = requestAnimationFrame(frame);
  }
  function stop() {
    if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
  }

  function resize() {
    const w = container.clientWidth || window.innerWidth;
    const h = container.clientHeight || window.innerHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    const bufH = h * renderer.getPixelRatio();
    snowResolution.set(w * renderer.getPixelRatio(), bufH);
    const pixelScale = bufH / (2 * tanV);
    const maxPt = Math.min(maxPointHW, bufH * 0.4);
    for (const L of layers) updateLayer(L, pixelScale, maxPt);
    if (rafId === null) step(0);
  }

  /* ----- Events ----- */
  const ro = new ResizeObserver(resize);
  ro.observe(container);
  const onPointerMove = (e) => {
    if (e.pointerType !== "mouse") return;
    mouse.tx = (e.clientX / window.innerWidth) * 2 - 1;
    mouse.ty = (e.clientY / window.innerHeight) * 2 - 1;
  };
  const onMouseLeave = () => { mouse.tx = 0; mouse.ty = 0; };
  const onVisibility = () => {
    if (!CONFIG.PAUSE_WHEN_HIDDEN) return;
    if (document.hidden) stop(); else start();
  };
  const onReducedChange = (e) => { reducedMotion = e.matches; stop(); start(); };

  window.addEventListener("pointermove", onPointerMove, { passive: true });
  document.documentElement.addEventListener("mouseleave", onMouseLeave);
  document.addEventListener("visibilitychange", onVisibility);
  reducedMQ.addEventListener?.("change", onReducedChange);

  resize();
  step(0);
  requestAnimationFrame(() => container.classList.add("is-ready"));
  start();

  /* ----- Cleanup (runs when the login screen unmounts) ----- */
  const dispose = () => {
    stop();
    ro.disconnect();
    window.removeEventListener("pointermove", onPointerMove);
    document.documentElement.removeEventListener("mouseleave", onMouseLeave);
    document.removeEventListener("visibilitychange", onVisibility);
    reducedMQ.removeEventListener?.("change", onReducedChange);
    disposables.forEach((d) => d.dispose());
    renderer.dispose();
    renderer.forceContextLoss();
    renderer.domElement.remove();
    container.classList.remove("is-ready");
  };

  return { dispose, gust: triggerGust };
}

/* ---------------------------------------------------------------------
   React component
   --------------------------------------------------------------------- */
// Usage:  <SnowBackground ref={snowRef} />   then   snowRef.current?.gust()
const SnowBackground = forwardRef(function SnowBackground({ className = "login-snow-3d" }, ref) {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const scene = createSnowScene(containerRef.current);
    sceneRef.current = scene;
    return () => {
      scene.dispose();
      sceneRef.current = null;
    };
  }, []);

  useImperativeHandle(ref, () => ({
    gust: () => sceneRef.current?.gust(),
  }), []);

  return <div ref={containerRef} className={className} aria-hidden="true" />;
});

export default SnowBackground;
